import axios from 'axios';
import moment from 'moment';
import { nameFromEmail, shortNameFromEmail } from 'src/utils/users.utils';
import { MyDocumentsType } from 'src/types/main';
import store from 'src/store';
import { selectCurrentUser } from 'src/modules/users/selector';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { isPWA } from "src/utils/mobile";

export type ElasticSearchDocumentItem = {
  id: number;
  documentId: string;
  Title: string;
  Code: string;
  Index: string;
  Critical: boolean;
  Path: string;
  PublicationDate: string | null;
  DateForRevision: string | null;
  ResponsibleForRevisionEmail: string | null;
  AuthorEmail: string | null;
  Ver: number;
  Version: string;
  version: number;
  MainDocument: { id: number; Title: string } | null;
  Collection: { id: number; Title: string; isPrivate?: boolean | null } | null;
  Department: { id: number; Title: string } | null;
  Column: { id: number; Title: string } | null;
  ColumnBlockArea: { id: number; Title: string } | null;
  DocType: { id: number; Title: string } | null;
  ResponsibleForRevision: { id: number; Name: string; Email: string } | null;
  CardStatus: { id: number; Title: string } | null;
  searchMeta: {
    DocumentFileId: number | null;
    foundBy: 'title' | 'content' | 'both';
    highlightedText?: string;
    elasticsearchId: string;
    elasticsearchScore: number;
  };
};

const getElasticBaseUrl = (): string => {
  if (!isPWA()) {
    return 'https://supply-mobile.napitkivmeste.ru/api/elasticsearch';
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api/elasticsearch`;
  }

  return 'https://supply.napitkivmeste.ru/api/elasticsearch';
};

const BASE_URL = getElasticBaseUrl();
const SEARCH_BY_TITLE_URL = `${BASE_URL}/enhanced-search-by-title`;
const SEARCH_BY_TEXT_URL = `${BASE_URL}/enhanced-search-by-text`;
// const ELASTICSEARCH_URL = 'https://supplyportal.srv.deareditor.ru/api/elasticsearch/enhanced-search';

const convertMarkToHighlight = (text: string): string => {
  if (!text) return '';
  return text.replace(/<mark>/g, '[highlight]').replace(/<\/mark>/g, '[/highlight]');
};

const makeLookup = (data: { id: number; Title: string } | null) => {
  if (!data) return { get_lookupId: () => 0, get_lookupValue: () => '' };
  return {
    id: data.id,
    Title: data.Title,
    get_lookupId: () => data.id,
    get_lookupValue: () => data.Title,
  };
};

const makeEmailLookup = (data: { id: number; Name: string; Email: string } | null) => {
  if (!data) return { get_lookupId: () => 0, get_lookupValue: () => '', get_email: () => '' };
  return {
    id: data.id,
    Name: data.Name,
    Email: data.Email,
    get_lookupId: () => data.id,
    get_lookupValue: () => data.Name,
    get_email: () => data.Email?.toLowerCase().trim() || '',
  };
};

const filterPrivateCollectionItems = (items: ElasticSearchDocumentItem[]): ElasticSearchDocumentItem[] => {
  const currentUser = selectCurrentUser(store.getState());
  if (!currentUser.id) return items;

  if (currentUser.isAdmin) return items;

  const userCollectionIds = new Set<number>([
    ...(Array.isArray(currentUser.isCollectionAdmin) ? currentUser.isCollectionAdmin : []),
    ...(Array.isArray(currentUser.isCollectionEditor) ? currentUser.isCollectionEditor : []),
  ]);

  return items.filter((item) => {
    const collection = item.Collection;
    if (!collection) return true;

    if (collection.isPrivate !== true) return true;
    return userCollectionIds.has(collection.id);
  });
};

const fetchElasticItems = async (
  url: string,
  query: string,
  allStatuses?: boolean,
  from?: number,
  pagesize: number = 100,
): Promise<ElasticSearchDocumentItem[]> => {
  let token: string | null = null;

  if (!isPWA()) {
    const result = await SecureStoragePlugin.get({ key: 'accessToken' });
    token = result.value;
  }

  const headers = {
    Accept: "application/json;odata=nometadata",
    "Content-Type": "application/json;odata=nometadata",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const response = await axios.get<ElasticSearchDocumentItem[]>(url, {
    params: {
      query,
      pagesize,
      ...(from !== undefined && from > 0 && { from }),
      ...(allStatuses && { allStatuses: true }),
    },
    headers,
    withCredentials: true,
  });
  const items = Array.isArray(response.data) ? response.data : [];
  return filterPrivateCollectionItems(items);
};

export const elasticSearchByTitle = async (query: string, from?: number): Promise<MyDocumentsType[]> => {
  const items = await fetchElasticItems(SEARCH_BY_TITLE_URL, query, undefined, from);
  return items.map((item) => mapElasticDocumentToMyDocument(item));
};

export const elasticSearchByText = async (query: string, from?: number, pagesize: number = 100): Promise<MyDocumentsType[]> => {
  const items = await fetchElasticItems(SEARCH_BY_TEXT_URL, query, undefined, from, pagesize);
  return items.map((item) => mapElasticDocumentToMyDocument(item));
};

export const elasticSearchByTitleRaw = async (query: string, from?: number): Promise<any[]> => {
  const items = await fetchElasticItems(SEARCH_BY_TITLE_URL, query, true, from);
  return items.map(mapElasticDocumentToRaw);
};

export const elasticSearchByTextRaw = async (query: string, from?: number): Promise<any[]> => {
  const items = await fetchElasticItems(SEARCH_BY_TEXT_URL, query, true, from);
  return items.map(mapElasticDocumentToRaw);
};

const mapElasticDocumentToRaw = (item: ElasticSearchDocumentItem) => ({
  ID: item.id,
  Code: item.Code,
  Index: item.Index,
  Title: item.Title,
  Critical: item.Critical ?? false,
  Path: item.Path,
  PublicationDate: item.PublicationDate,
  DateForRevision: item.DateForRevision,
  AuthorEmail: item.AuthorEmail,
  ResponsibleForRevisionEmail: item.ResponsibleForRevisionEmail,
  Ver: item.Ver,
  Collection: makeLookup(item.Collection),
  Department: makeLookup(item.Department),
  Sector: makeLookup(item.Column),
  ColumnBlockArea: makeLookup(item.ColumnBlockArea),
  DocType: makeLookup(item.DocType),
  ResponsibleForRevision: makeEmailLookup(item.ResponsibleForRevision),
  Author: makeEmailLookup(item.ResponsibleForRevision),
  Status: makeLookup(item.CardStatus),
  MainDocument: item.MainDocument ? makeLookup(item.MainDocument) : null,
  version: item?.Ver,
  Version: item?.Ver,
  Lines: null,
  lines: [],
  eq: [],
  SAP: null,
  ManufacturerEquipments: null,
  ModelEquipments: null,
  TypeEquipments: null,
  searchMeta: item.searchMeta
    ? {
        ...item.searchMeta,
        highlightedText: item.searchMeta.highlightedText
          ? convertMarkToHighlight(item.searchMeta.highlightedText)
          : '',
      }
    : undefined,
});

const mapElasticDocumentToMyDocument = (item: ElasticSearchDocumentItem): MyDocumentsType => {
  const email = item.ResponsibleForRevisionEmail || item.ResponsibleForRevision?.Email || '';
  const meta = item.searchMeta;

  const titleForDisplay =
    meta?.foundBy === 'title' && meta?.highlightedText
      ? convertMarkToHighlight(meta.highlightedText)
      : item.Title || '';

  return {
    id: item.id,
    code: {
      code: `${item.Code || ''}.${item.Index || ''}`,
      name: titleForDisplay,
    },
    aboutDocument: {
      collection: item.Collection?.Title || '',
      department: item.Department?.Title || '',
      sector: item.Column?.Title || '',
      process: item.ColumnBlockArea?.Title || '',
      type: item.DocType?.Title || '',
    },
    responsibleForRevision: {
      name: email ? nameFromEmail(email) : '',
      avatar: null,
      email,
      shortName: email ? shortNameFromEmail(email) : 'ND',
    },
    publicationDate: item.PublicationDate ? moment(item.PublicationDate).locale('ru').format('DD.MM.YYYY') : '',
    critical: item.Critical ?? false,
    searchMeta: {
      DocumentFileId: meta?.DocumentFileId ?? null,
      foundBy: meta?.foundBy || 'title',
      highlightedText: meta?.highlightedText ? convertMarkToHighlight(meta.highlightedText) : '',
      elasticsearchId: meta?.elasticsearchId || '',
      elasticsearchScore: meta?.elasticsearchScore ?? 0,
    },
  };
};
